/**
 * Core question counter — single function that counts gradable answer units
 * for ANY content type by slug.
 *
 * This is the **source of truth** used by:
 *   - ContentPlayer (to show "X / Y" progress or numbered nav)
 *   - shared-api/countSubQuestions (metadata sync)
 *   - Backend Content.count_sub_questions() (mirrors this logic)
 *
 * Counting rules (matching FE scorer `childTotal` / `totalItems`):
 *   - MCQ          → 1
 *   - Match        → number of match pairs (items)
 *   - FITB         → number of blanks
 *   - Ordering     → number of ordering items
 *   - Essay        → 1
 *   - Documents    → 0
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const asArr = (v: unknown): unknown[] | null =>
    Array.isArray(v) ? v : null;

const asObj = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === 'object' && !Array.isArray(v)
        ? (v as Record<string, unknown>)
        : null;

// ---------------------------------------------------------------------------
// Per-type counters (private)
// ---------------------------------------------------------------------------

/** MCQ Set: each MCQ item = 1 */
function countMcqSet(data: Record<string, unknown>): number {
    return asArr(data.items)?.length ?? 0;
}

/**
 * Question Set: sum child answer units per item sub-type.
 *   mcq      → 1
 *   match    → items.length
 *   fitb-*   → blanks.length
 *   ordering → items.length
 */
function countQuestionSet(data: Record<string, unknown>): number {
    const items = asArr(data.items);
    if (!items) return 0;
    let count = 0;
    for (const raw of items) {
        const item = asObj(raw);
        if (!item) continue;
        const type = item.type as string;
        const itemData = asObj(item.data) ?? {};
        switch (type) {
            case 'mcq':
                count += 1;
                break;
            case 'match':
                count += asArr(itemData.items)?.length ?? 0;
                break;
            case 'fitb-dropdown':
            case 'fitb-type':
                count += asArr(itemData.blanks)?.length ?? 0;
                break;
            case 'ordering':
                count += asArr(itemData.items)?.length ?? 0;
                break;
            default:
                count += 1; // unknown sub-type fallback
                break;
        }
    }
    return count;
}

/**
 * Quiz: only gradeable MCQ items.
 * Mirrors getGradeableMcqItems: gradeable !== false AND type === 'mcq'.
 */
function countQuiz(data: Record<string, unknown>): number {
    const sections = asArr(data.sections);
    let allItems: unknown[];
    if (sections && sections.length > 0) {
        allItems = [];
        for (const sec of sections) {
            const secObj = asObj(sec);
            if (secObj) {
                const secItems = asArr(secObj.items);
                if (secItems) allItems.push(...secItems);
            }
        }
    } else {
        allItems = asArr(data.items) ?? [];
    }
    let count = 0;
    for (const raw of allItems) {
        const item = asObj(raw);
        if (!item) continue;
        if (item.gradeable === false) continue;
        if (item.type === 'mcq') count += 1;
    }
    return count;
}

/** Lesson MCQ: questionSet.items.length */
function countLessonMcq(data: Record<string, unknown>): number {
    const qs = asObj(data.questionSet);
    return qs ? (asArr(qs.items)?.length ?? 0) : 0;
}

/** Fill-in-the-Blanks: sum of blanks across exercises */
function countFITB(data: Record<string, unknown>): number {
    const exercises = asArr(data.exercises);
    if (!exercises) return 0;
    let count = 0;
    for (const raw of exercises) {
        const ex = asObj(raw);
        if (!ex) continue;
        const exData = asObj(ex.data);
        if (exData) count += asArr(exData.blanks)?.length ?? 0;
    }
    return count;
}

/** Ordering: sum of items across exercises */
function countOrdering(data: Record<string, unknown>): number {
    const exercises = asArr(data.exercises);
    if (!exercises) return 0;
    let count = 0;
    for (const raw of exercises) {
        const ex = asObj(raw);
        if (!ex) continue;
        const exData = asObj(ex.data);
        if (exData) count += asArr(exData.items)?.length ?? 0;
    }
    return count;
}

/** Speaking: each recording prompt = 1 */
function countSpeaking(data: Record<string, unknown>): number {
    return asArr(data.items)?.length ?? 0;
}

/** Qt-Dropdown: sum of blanks across all items */
function countDropdown(data: Record<string, unknown>): number {
    const items = asArr(data.items);
    if (!items) return 0;
    let count = 0;
    for (const raw of items) {
        const item = asObj(raw);
        if (!item) continue;
        const itemData = asObj(item.data);
        if (itemData) count += asArr(itemData.blanks)?.length ?? 0;
    }
    return count;
}

/** Rich Matching: matchData.items.length */
function countRichMatching(data: Record<string, unknown>): number {
    const md = asObj(data.matchData);
    return md ? (asArr(md.items)?.length ?? 0) : 0;
}

/** Image Hotspot: colorDropZoneData.objects.length */
function countImageHotspot(data: Record<string, unknown>): number {
    const cdz = asObj(data.colorDropZoneData);
    return cdz ? (asArr(cdz.objects)?.length ?? 0) : 0;
}

// ---------------------------------------------------------------------------
// Slug → counter map
// ---------------------------------------------------------------------------

const COUNTERS: Record<string, (data: Record<string, unknown>) => number> = {
    'mcq-set': countMcqSet,
    'question-set': countQuestionSet,
    'quiz': countQuiz,
    'lesson-mcq': countLessonMcq,
    'fill-in-the-blanks': countFITB,
    'ordering': countOrdering,
    'speaking': countSpeaking,
    'qt-dropdown': countDropdown,
    'rich-matching': countRichMatching,
    'image-hotspot': countImageHotspot,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Count the total number of gradable answer units for a content item.
 *
 * This is the **single source of truth** — all layers (player, editor,
 * metadata, backend) should use or mirror this logic.
 *
 * @param slug  Content-type slug (e.g. 'mcq-set', 'question-set', 'quiz')
 * @param data  The content data JSON object
 * @returns     Number of gradable answer units (≥ 0)
 *
 * @example
 * ```ts
 * import { countQuestions } from '@ai-content-hub/content-core';
 *
 * const total = countQuestions('question-set', data);
 * // → e.g. 10 (1 mcq + 3 match pairs + 4 fitb blanks + 2 ordering items)
 * ```
 */
export function countQuestions(
    slug: string,
    data: Record<string, unknown> | null | undefined,
): number {
    if (!data || !slug) return 0;

    // Known types
    const counter = COUNTERS[slug];
    if (counter) return counter(data);

    // Special constants
    if (slug === 'essay') return 1;
    if (slug === 'documents') return 0;

    // Unknown type — generic fallback
    for (const key of ['items', 'exercises']) {
        const v = asArr(data[key]);
        if (v) return v.length;
    }
    return 0;
}

/**
 * Register a custom counter for a content-type slug.
 *
 * Use this when adding a new content type to extend the counting
 * without modifying this file.
 */
export function registerQuestionCounter(
    slug: string,
    counter: (data: Record<string, unknown>) => number,
): void {
    COUNTERS[slug] = counter;
}

/**
 * Get the list of content-type slugs that have registered counters.
 */
export function getRegisteredCounterSlugs(): string[] {
    return Object.keys(COUNTERS);
}
